import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/i18n/I18nProvider";

const HEADER_HEIGHT = 72;

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_HEIGHT;
  window.scrollTo({ top: y, behavior: "auto" });
}

export default function Header() {
  const { t, language, switchLanguage, buildLocalizedPath } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const home = buildLocalizedPath("");
  const onHome = location.pathname === home;

  const sectionLinks = [
    { key: "story", label: t("nav.story"), id: "story" },
    { key: "reserve", label: t("nav.reserve"), id: "reserve" },
    { key: "heritage", label: t("nav.heritage"), id: "heritage" },
    { key: "contact", label: t("nav.contact"), id: "newsletter" },
  ];

  const handleSectionClick = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (onHome) {
      scrollToId(id);
    } else {
      navigate(home, { state: { scrollTo: id } });
    }
  };

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-stone-800"
      style={{ backgroundColor: "#1C241C", color: "#FAF8F5" }}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 lg:px-10" style={{ height: HEADER_HEIGHT }}>
        <Link
          to={home}
          aria-label={t("aria.logo")}
          data-testid="header-logo-link"
          className="flex items-center gap-3"
        >
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-stone-500"
            style={{ backgroundColor: "#2D3B2D" }}
          >
            <span
              className="font-serif text-lg"
              style={{ color: "#C59B27", letterSpacing: "0.05em" }}
            >
              B
            </span>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl tracking-wide" style={{ color: "#FAF8F5" }}>
              {t("brand.name")}
            </span>
            <span
              className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: "#C59B27" }}
            >
              {t("brand.tagline")}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {sectionLinks.map((l) => (
            <Link
              key={l.key}
              to={home}
              onClick={(e) => handleSectionClick(e, l.id)}
              data-testid={`header-nav-${l.key}`}
              className="font-sans text-sm tracking-wide hover:text-white"
              style={{ color: "#E7E2D8" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-1 rounded-sm border border-stone-600 px-1 py-1" data-testid="header-language-switcher">
            {SUPPORTED_LANGS.map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => switchLanguage(lng)}
                data-testid={`header-lang-${lng}`}
                aria-pressed={language === lng}
                className="px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.2em]"
                style={{
                  backgroundColor: language === lng ? "#C59B27" : "transparent",
                  color: language === lng ? "#1C241C" : "#E7E2D8",
                }}
              >
                {lng === "it" ? t("language.short_it") : t("language.short_en")}
              </button>
            ))}
          </div>
          <Link
            to={home}
            onClick={(e) => handleSectionClick(e, "newsletter")}
            data-testid="header-cta-subscribe"
            className="rounded-sm px-4 py-2 font-sans text-sm tracking-wide"
            style={{ backgroundColor: "#C59B27", color: "#1C241C" }}
          >
            {t("nav.subscribe")}
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm border border-stone-600"
          aria-label={open ? t("aria.close_menu") : t("aria.open_menu")}
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
          style={{ color: "#FAF8F5" }}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-stone-800"
          style={{ backgroundColor: "#1C241C" }}
          data-testid="header-mobile-menu"
        >
          <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-6">
            <nav className="flex flex-col gap-4" aria-label="Mobile primary">
              {sectionLinks.map((l) => (
                <Link
                  key={l.key}
                  to={home}
                  onClick={(e) => handleSectionClick(e, l.id)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="font-serif text-2xl"
                  style={{ color: "#FAF8F5" }}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center justify-between border-t border-stone-800 pt-6">
              <div className="flex items-center gap-1 rounded-sm border border-stone-600 px-1 py-1">
                {SUPPORTED_LANGS.map((lng) => (
                  <button
                    key={lng}
                    type="button"
                    onClick={() => {
                      switchLanguage(lng);
                      setOpen(false);
                    }}
                    data-testid={`mobile-lang-${lng}`}
                    className="px-3 py-1 font-mono text-xs uppercase tracking-[0.2em]"
                    style={{
                      backgroundColor: language === lng ? "#C59B27" : "transparent",
                      color: language === lng ? "#1C241C" : "#E7E2D8",
                    }}
                  >
                    {lng === "it" ? t("language.short_it") : t("language.short_en")}
                  </button>
                ))}
              </div>
              <Link
                to={home}
                onClick={(e) => handleSectionClick(e, "newsletter")}
                data-testid="mobile-cta-subscribe"
                className="rounded-sm px-4 py-2 font-sans text-sm tracking-wide"
                style={{ backgroundColor: "#C59B27", color: "#1C241C" }}
              >
                {t("nav.subscribe")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
